import React, { useEffect, useState } from "react";

import { withTranslation } from "react-i18next";

import { Box, IconButton, InputBase, Menu, MenuItem } from "@mui/material";
import { isEmpty, isEqual, size } from "lodash";

import MoreVertIcon from "@mui/icons-material/MoreVert";
// 헤더 표식과 이 패널을 여는 툴바 버튼은 같은 아이콘을 쓴다 — 누른 버튼과 열린 패널이 이어져 보이게.
// (툴바 쪽: Workbench / RpReview / PsiSimulation 세 화면)
import EditNoteIcon from "@mui/icons-material/EditNote";
import ForumIcon from "@mui/icons-material/Forum";
import SaveIcon from "@mui/icons-material/Save";

import SidePanel, {
  PANEL,
  PANEL_LABEL_SX,
  PanelPrimaryButton,
} from "@plannel/components/ui/SidePanel";

import dpNoteService from "@plannel/services/dp/dp-note-service";
import rpNoteSerive from "@plannel/services/system/note-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils, { DATETIME_MINUTE_FORMAT } from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";

const NotesModal = props => {
  const { t, timeDimension, cellData, setUpdateCellData, updateFlg, pageCd, close } = props;
  const { SIM_DEMAND_QTY, SIM_BOH, SIM_BACKORDER_QTY, SIM_ARRIVAL_QTY, SIM_SHIPMENT_QTY, SIM_EOH, SIM_INV_COV_DAYS,
    DEMAND_QTY, ARRIVAL_QTY, SHIPMENT_QTY, RP_ORDER, REPLENISH_PLAN_QTY, PURCHASE_PLAN_QTY, BOH, EOH,  BACKORDER_QTY, INV_COV_DAYS } = CodeUtil.rpMeasure;

  const [cellDate, setCellDate] = useState("");
  const [cellNote, setCellNote] = useState("");
  const [cellMeasure, setCellMeasure] = useState({});
  const [cellItem, setCellItem] = useState("");
  const [cellCustomer, setCellCustomer] = useState("");
  const [disableFlg, setDisableFlg] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const [noteData, setNoteData] = useState([]);
  const [standardData, setStandardData] = useState({});
  const [selectedRow, setSelectedRow] = useState("");

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const { snackBarMessageColor } = ColorUtil;

  const open = Boolean(anchorEl);
  const { customer, item, dpP13N: { DP_WORKBENCH } } = CodeUtil;
  const CHANNEL_WORKBENCH = "DP_CHANNEL_WORKBENCH";
  const isDp = isEqual(pageCd, DP_WORKBENCH) || isEqual(pageCd, CHANNEL_WORKBENCH);
  // MP 는 RP 와 같은 z_note 를 쓴다(loc = 공장). 키 모양만 다르다 — CodeUtil.getNoteKey 참고
  const isMp = CodeUtil.MP_NOTE_PAGES.includes(pageCd);
  const noteService = isDp ? dpNoteService : rpNoteSerive;

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = value => () => {
    setSelectedRow(value.key);
    for (const row of noteData) {
      if (row.key === value.key && row.seqNum === value.seqNum) {
        setStandardData(row);
        setCellNote(row.contentTxt);
        setCellDate(row.headerDate);
      }
    }
  };

  const getRowData = row => {
    if (isDp) {
      row.item = cellData.cellTarget.itemHrchyNm;
      row.customer = cellData.cellTarget.customerHrchyNm;
      row.headerDate = dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension);
      row.key = row.id;
      row.measure = cellData.cellTarget.measure;
      row.baseDate = cellData.cellDate;
    } else if (isMp) {
      row.item = cellData.itemNm;
      row.locCd = cellData.locCd;
      row.headerDate = cellData.cellDate ? dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension) : "";
      row.key = row.id;
      row.measure = cellData.measure;
      row.baseDate = cellData.cellDate;
    } else {
      row.item = cellData.itemNm;
      row.locCd = cellData.originLocCd;
      row.originLoc = cellData.originLocNm;
      row.destLoc = cellData.destLocNm;
      row.headerDate = cellData.cellDate ? dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension) : "";
      row.key = row.id;
      row.measure = cellData.measure;
      row.baseDate = cellData.cellDate;
    }
  };

  const handleLoadData = data => {
    const measure = isDp ? cellData.cellTarget.measure : cellData.measure;
    setCellMeasure(measure);
    
    if (data) {
      for (const row of data) {
        getRowData(row);
      }
      setNoteData(data);
    } else {
      setNoteData([]);
    }
  };

  const retrieveNoteData = () => {
    setNoteData([]);
    if (size(cellData) > 0) {
      const cellParams = {};
      if (isDp) {
        const noteKey = CodeUtil.getNoteKey(pageCd, cellData.cellTarget);
        setCellCustomer(cellData.cellTarget?.customerHrchyNm);
        setCellItem(cellData.cellTarget?.itemHrchyNm);
        setCellDate(dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension));
        setCellNote("");
        setStandardData({
          planVersion: cellData.cellTarget?.versionDto,
          baseDate: cellData.cellDate,
          headerDate: dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension),
          customerCd: cellData?.selectCL === customer.cd ? cellData.cellTarget.customerCd : null,
          customerHrchyCd: cellData?.selectCL !== customer.cd ? cellData.cellTarget?.customerHrchyCd : null,
          itemCd: cellData?.selectIL === item.cd ? cellData.cellTarget.itemCd : null,
          itemHrchyCd: cellData?.selectIL !== item.cd ? cellData.cellTarget.itemHrchyCd : null,
          measure: cellData.cellTarget.measure,
          noteKey,
          seqNum: 0,
        });
        cellData.cellTarget.selectedDate = cellData.cellDate;
        cellData.cellTarget.noteKey = noteKey;
        cellData.cellTarget.measure.formula = null;
        cellData.cellTarget.measure.formulaExp = null;
      } else if (isMp) {
        const noteKey = CodeUtil.getNoteKey(pageCd, cellData);
        const displayDate = cellData.cellDate ? dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension) : "";
        setCellCustomer(cellData?.locNm);
        setCellItem(cellData?.itemNm);
        setCellDate(displayDate);
        setCellNote("");
        setStandardData({
          planVersion: cellData?.versionDto,
          planVersionId: cellData?.versionDto?.id,
          baseDate: cellData.cellDate,
          headerDate: displayDate,
          itemCd: cellData.itemCd,
          locCd: cellData.locCd,
          measure: cellData?.measure,
          noteKey,
          seqNum: 0,
        });
        cellParams.selectedDate = cellData.cellDate;
        cellParams.noteKey = noteKey;
        cellParams.planVersionDto = cellData?.versionDto;
        cellParams.planVersionId = cellData?.versionDto?.id;
      } else {
        if (isEqual(pageCd, "RP_REVIEW")) {
          const measures = cellData?.originMeasure ?? cellData?.measure;
          if (isEqual(measures, "demandQty")) cellData.originMeasure = "simDemandQty";
          if (isEqual(measures, "boh")) cellData.originMeasure = "simBoh";
          if (isEqual(measures, "eoh")) cellData.originMeasure = "simEoh";
          if (isEqual(measures, "backorderQty")) cellData.originMeasure = "simBackorderQty";
          if (isEqual(measures, "arrivalQty")) cellData.originMeasure = "simArrival";
          if (isEqual(measures, "shipmentQty")) cellData.originMeasure = "simShipmentQty";
          if (isEqual(measures, "inventoryCoverageDays")) cellData.originMeasure = "simInventoryCoverageDays";
        }

        const noteKey = CodeUtil.getNoteKey(pageCd, cellData);
        setCellCustomer(cellData?.destLocNm);
        setCellItem(cellData?.itemNm);
        // locationItem level note는 baseDate/measure 가 없으므로 날짜 포맷 생략
        const displayDate = cellData.cellDate ? dateUtils.headerDateFormatter(new ZDate(cellData.cellDate), cellData.timeDimension) : "";
        setCellDate(displayDate);
        setCellNote("");
        setStandardData({
          planVersion: cellData?.versionDto,
          planVersionId: cellData?.versionDto?.id,
          baseDate: cellData.cellDate,
          headerDate: displayDate,
          itemCd: cellData.itemCd,
          locCd: cellData.originLocCd,
          destLocCd: cellData.destLocCd,
          measure: cellData?.measure,
          originMeasure: cellData?.originMeasure,
          noteKey,
          seqNum: 0,
        });
        cellParams.selectedDate = cellData.cellDate;
        cellParams.noteKey = noteKey;
        cellParams.planVersionDto = cellData?.versionDto;
        cellParams.planVersionId = cellData?.versionDto?.id;
      }
      const params = isDp ? cellData.cellTarget : cellParams;
      noteService
        .getNote(params)
        .then(response => {
          handleLoadData(response.data.noteResult);
        })
        .catch(e => {
          debug(e);
        });
    }
  };

  const handleSaveClick = () => {
    if (updateFlg) updateFlg(true);

    standardData.contentTxt = cellNote;
    if (!standardData?.id) {
      let topSeq = 0;
      for (const row of noteData) {
        if (row.baseDate === standardData.baseDate) {
          if (topSeq < row.seqNum) {
            topSeq = row.seqNum;
          }
        }
      }
      standardData.seqNum = topSeq + 1;
    }
    noteService.upsertNote(standardData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setCellNote("");
        retrieveNoteData();
        if (isDp) {
          const index = cellData?.cellMatrix[1];
          cellData.cellTarget.noteFlg[index] = true;
        }
        setUpdateCellData(cellData);
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
      });
  };

  const handleEditClick = () => {
    for (const row of noteData) {
      if (row.key === selectedRow) {
        setStandardData(row);
        setCellNote(row.contentTxt);
        setCellDate(row.headerDate);
      }
    }

    handleClose();
  };

  const handleDeleteClick = () => {
    if (updateFlg) updateFlg(true);
    for (const row of noteData) {
      if (row.key === selectedRow) {
        noteService.deleteNote(row)
          .then(() => {
            setCellNote("");
            if (isDp) {
              if (noteData.length <= 1) {
                const index = cellData?.cellMatrix[1];
                cellData.cellTarget.noteFlg[index] = false;
                setUpdateCellData(cellData);
              }
            } else {
              if (size(noteData) === 1) {
                cellData.deleteFlg = true;
              }
              setUpdateCellData(cellData);
            }
            handleClose();
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
          })
          .catch(e => {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
            debug(e);
          })
          .then(() => {
            retrieveNoteData();
          });
      }
    }
  };

  const handleDelete = () => {
    setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") });
  };

  const handleDialog = action => {
    if (action === "DELETE") {
      handleDeleteClick();
    }
  };

  const onChangeNote = e => {
    setCellNote(e.target.value);
  };

  useEffect(() => {
    if (size(cellData) > 0) {
      if (isDp) {
        if (cellData.selectCL === customer.cd) {
          setDisableFlg(false);
        } else {

          const readable = cellData?.cellTarget?.readable;
          const writable = cellData?.cellTarget?.writable;
          const hrchy = cellData?.cellTarget?.customerHrchyCd;

          if (readable || writable) {
            if (readable.includes(hrchy) || writable.includes(hrchy)) {
              setDisableFlg(false);
            } else {
              setDisableFlg(true);
            }
          } else {
            setDisableFlg(false);
          }
        }
      } else {
        if (isEqual(pageCd, "PSI_SIMULATION")) {
          const disableMeasures = [DEMAND_QTY, ARRIVAL_QTY, SHIPMENT_QTY, RP_ORDER, REPLENISH_PLAN_QTY, PURCHASE_PLAN_QTY, BOH, EOH, BACKORDER_QTY, INV_COV_DAYS];
          if (disableMeasures.includes(cellData?.measure)) {
            setDisableFlg(true);
          } else {
            setDisableFlg(false);
          }
        } else {
          setDisableFlg(false);
        }
      }
    }
    retrieveNoteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellData]);

  /* 컨텍스트 그리드 첫 칸의 이름 — DP 는 고객, RP 계열은 도착지, MP 는 공장이다. */
  const firstDimLabel = isDp ? t("customer") : isMp ? t("plant") : t("destLocation");

  /*
   * 저장 버튼 노출 조건. 원래 코드는
   *   version?.planConfigObj?.BUCKET_UNIT === isObject(timeDimension) ? timeDimension.current : timeDimension ? …
   * 였는데 왼쪽 비교는 문자열 vs 불리언이라 참이 될 수 없고(참이면 객체를 렌더해 터진다),
   * 실제로는 timeDimension 의 truthy 여부만 보고 있었다. 그 동작을 그대로 남긴다.
   */
  const canWriteNote = Boolean(timeDimension) && !disableFlg;

  /* 아바타 이니셜 — 작성자 이름의 첫 두 글자(공백으로 나뉘면 각 토큰의 첫 글자). */
  const getInitials = name => {
    if (!name) {
      return "?";
    }
    const parts = String(name).trim().split(/\s+/);
    return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2)).toUpperCase();
  };

  const getMeasureNm = () => {
    let measureNm = isEmpty(cellMeasure) ? "" : cellMeasure?.name ?? cellMeasure;
    if (isEqual(pageCd, "RP_REVIEW")) {

      if (isEqual(measureNm, SIM_DEMAND_QTY)) measureNm = DEMAND_QTY;
      if (isEqual(measureNm, SIM_BOH)) measureNm = BOH;
      if (isEqual(measureNm, SIM_BACKORDER_QTY)) measureNm = BACKORDER_QTY;
      if (isEqual(measureNm, SIM_ARRIVAL_QTY)) measureNm = ARRIVAL_QTY;
      if (isEqual(measureNm, SIM_SHIPMENT_QTY)) measureNm = SHIPMENT_QTY;
      if (isEqual(measureNm, SIM_EOH)) measureNm = EOH;
      if (isEqual(measureNm, SIM_INV_COV_DAYS)) measureNm = INV_COV_DAYS;
    }
    return GridUtils.gridValueL10N(measureNm);
  };

  /*
   * 헤더 부제 — 셀을 고르지 않았으면 아예 비운다.
   * 값 없이 라벨만 남으면("Measure") 무엇을 편집 중인지 말해 주기는커녕 오해만 만든다.
   * (레거시 헤더도 같은 자리에 `Measure : ` 를 값 없이 찍고 있었다 — 그때부터의 문제다.)
   */
  const measureNm = getMeasureNm();
  const measureSubtitle = measureNm ? `${t("measure")} · ${measureNm}` : null;

  return (
    <>
      <SidePanel
        id="dp-note-panel"
        resizable
        icon={<EditNoteIcon />}
        title={t("notes")}
        subtitle={measureSubtitle}
        onClose={close}
        closeLabel={t("close")}
        bodySx={{ gap: "13px" }}>

        {/* 선택한 셀이 무엇인지 — 시안의 3칸 컨텍스트 그리드. 1px 간격에 회색 배경을 깔아 구분선을 만든다. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "1px",
            bgcolor: PANEL.line,
            borderRadius: "12px",
            overflow: "hidden",
            flex: "none",
          }}>
          {[
            { label: firstDimLabel, value: cellData ? cellCustomer : "" },
            { label: t("item"), value: cellData ? cellItem : "" },
            { label: t("time"), value: cellDate },
          ].map(cell => (
            <Box key={cell.label} sx={{ bgcolor: "#fafcfd", p: "10px 11px", minWidth: 0 }}>
              <Box sx={PANEL_LABEL_SX}>{cell.label}</Box>
              <Box
                sx={{
                  mt: "3px",
                  fontSize: FS.SUB,
                  color: PANEL.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {cell.value || "—"}
              </Box>
            </Box>
          ))}
        </Box>

        {/* 작성 영역 — 카드 안쪽에 테두리 없는 입력, 포커스는 카드 링으로 표시한다. */}
        <Box
          sx={{
            flex: "none",
            borderRadius: "12px",
            p: "13px",
            boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
            transition: "box-shadow .15s ease",
            "&:focus-within": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` },
          }}>
          <InputBase
            multiline
            minRows={3}
            disabled={disableFlg}
            value={cellNote ? cellNote : ""}
            onChange={onChangeNote}
            placeholder={t("MSG_PleaseEnterValues", { values: t("notes") })}
            sx={{
              width: "100%",
              p: 0,
              fontSize: FS.TITLE,
              lineHeight: 1.5,
              color: PANEL.ink,
              "& textarea::placeholder": { color: PANEL.inkFaint, opacity: 1 },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", mt: "12px" }}>
            <Box sx={{ flex: 1 }} />
            <PanelPrimaryButton
              icon={<SaveIcon />}
              disabled={!canWriteNote || isEmpty(cellNote)}
              onClick={handleSaveClick}>
              {t("save")}
            </PanelPrimaryButton>
          </Box>
        </Box>

        {/* 스레드 — 없으면 빈 상태 카드를 대신 보여준다. */}
        {size(noteData) > 0 ? (
          noteData.map(value => (
            <Box
              key={value.id}
              onClick={handleToggle(value)}
              sx={{
                flex: "none",
                display: "flex",
                gap: "11px",
                p: "12px 13px",
                borderRadius: "12px",
                cursor: "pointer",
                bgcolor: isEqual(selectedRow, value.key) ? PANEL.accentWash : PANEL.wellBg,
                boxShadow: isEqual(selectedRow, value.key)
                  ? `inset 0 0 0 1px ${PANEL.accentLine}`
                  : "none",
                transition: "background .15s ease, box-shadow .15s ease",
              }}>
              <Box
                sx={{
                  flex: "none",
                  width: 30,
                  height: 30,
                  borderRadius: "15px",
                  bgcolor: PANEL.accentDeep,
                  color: "#fff",
                  fontSize: FS.SUB,
                  fontWeight: 500,
                  lineHeight: "30px",
                  textAlign: "center",
                }}>
                {getInitials(value.createdUser)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                  <Box sx={{ fontSize: FS.SUB, fontWeight: 500, color: PANEL.ink }}>
                    {value.createdUser}
                  </Box>
                  {/* DB 원본은 `2026-08-27T10:34:37.0306547` 다 — 초·나노초까지 보여 줄 자리가 아니다. */}
                  <Box sx={{ fontSize: FS.CAPTION, color: PANEL.inkLabel, whiteSpace: "nowrap" }}>
                    {dateUtils.formatDate(value.updatedTs, DATETIME_MINUTE_FORMAT)}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ fontSize: FS.CAPTION, color: PANEL.inkLabel, whiteSpace: "nowrap" }}>
                    {value.headerDate}
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: "3px",
                    fontSize: FS.SUB,
                    lineHeight: 1.55,
                    color: PANEL.inkBody,
                    overflowWrap: "anywhere",
                  }}>
                  {value.contentTxt}
                </Box>
              </Box>
              {canWriteNote ? (
                <IconButton
                  size="small"
                  sx={{ flex: "none", alignSelf: "flex-start", p: 0.25, color: PANEL.iconFaint }}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggle(value)();
                    handleMenuClick(e);
                  }}>
                  <MoreVertIcon sx={{ fontSize: 19 }} />
                </IconButton>
              ) : null}
            </Box>
          ))
        ) : (
          <Box
            sx={{
              flex: "none",
              borderRadius: "12px",
              bgcolor: PANEL.wellBg,
              p: "28px",
              textAlign: "center",
            }}>
            <ForumIcon sx={{ fontSize: 26, color: "#c8d3da" }} />
            <Box sx={{ mt: "7px", fontSize: FS.SUB, color: PANEL.inkMuted }}>
              {t("MSG_NoData")}
            </Box>
          </Box>
        )}
      </SidePanel>

      <Menu
        id="note-row-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}>
        <MenuItem
          sx={{ fontSize: FS.TITLE }}
          onClick={handleEditClick}>
          {t("update")}
        </MenuItem>
        <MenuItem
          sx={{ fontSize: FS.TITLE }}
          onClick={handleDelete}>
          {t("delete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(NotesModal);
